import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Box,
  Typography,
  ButtonBase,
  Breadcrumbs,
  Link,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import DatasetOutlinedIcon from "@mui/icons-material/DatasetOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import { useUser } from "../contexts/UserContext";
import { MyProfile } from "../services/UsersService";
import { disconnectAdminAppointmentSocket } from "../services/adminAppointmentSocket";
import { logout } from "../services/authService";
import { COLORS } from "../assets/theme";
import logo from "../assets/softwarezz-icon.png";

export interface Crumb {
  label: string;
  to?: string;
}

interface TopBarProps {
  profile?: MyProfile;
  /** Shows the hamburger on phones (dashboard only). */
  onMenuClick?: () => void;
  /** Trail shown after the brand; the last entry is the current page. */
  crumbs?: Crumb[];
  position?: "fixed" | "static";
  sx?: object;
  toolbarSx?: object;
}

const formatRole = (role?: string) =>
  role
    ? role
        .toLowerCase()
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ")
    : "";

const TopBar: React.FC<TopBarProps> = ({
  profile,
  onMenuClick,
  crumbs = [],
  position = "fixed",
  sx,
  toolbarSx,
}) => {
  const navigate = useNavigate();
  const { user, setUser } = useUser();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const go = (path: string) => {
    setAnchorEl(null);
    navigate(path);
  };
  const handleLogout = async () => {
    setAnchorEl(null);
    disconnectAdminAppointmentSocket();
    setUser(null);
    await logout();
  };

  const name = profile?.fullName || user?.fullName || "";
  const email = profile?.email || user?.email || "";
  const role = formatRole(user?.role);
  const initial = name.trim().charAt(0).toUpperCase() || "U";
  const avatar = (size: number) => (
    <Avatar
      src={profile?.banner || undefined}
      alt={name || "Profile"}
      sx={{
        width: size,
        height: size,
        fontSize: size * 0.42,
        fontWeight: 600,
        bgcolor: COLORS.MainColor,
      }}
    >
      {initial}
    </Avatar>
  );
  const current = crumbs[crumbs.length - 1];

  return (
    <AppBar
      position={position}
      elevation={0}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        bgcolor: "rgba(255, 255, 255, 0.86)",
        backdropFilter: "saturate(180%) blur(10px)",
        color: COLORS.typographyColor,
        borderBottom: "1px solid",
        borderColor: "divider",
        ...sx,
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          px: { xs: 1, sm: 2 },
          ...toolbarSx,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, gap: 1 }}>
          {onMenuClick && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={onMenuClick}
              sx={{ display: { sm: "none" } }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <ButtonBase
            onClick={() => navigate("/")}
            aria-label="Go to home"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              borderRadius: 2,
              px: 0.75,
              py: 0.5,
              flexShrink: 0,
              transition: "background-color 0.2s",
              "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
            }}
          >
            <Box
              component="img"
              src={logo}
              alt=""
              sx={{ width: 26, height: 26, borderRadius: 1 }}
            />
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.05rem",
                letterSpacing: "-0.01em",
                // On phones the page title takes this spot instead.
                display: { xs: current ? "none" : "block", sm: "block" },
              }}
            >
              Dashboard
            </Typography>
          </ButtonBase>

          {crumbs.length > 0 && (
            <>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ my: 1.5, display: { xs: "none", sm: "block" } }}
              />
              <Breadcrumbs
                separator={<NavigateNextIcon sx={{ fontSize: "1rem" }} />}
                sx={{
                  display: { xs: "none", sm: "flex" },
                  minWidth: 0,
                  "& .MuiBreadcrumbs-ol": { flexWrap: "nowrap" },
                  "& .MuiBreadcrumbs-li": {
                    minWidth: 0,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                  "& .MuiBreadcrumbs-separator": { mx: 0.5 },
                }}
              >
                {crumbs.map((crumb, i) => {
                  const last = i === crumbs.length - 1;
                  if (last || !crumb.to) {
                    return (
                      <Typography
                        key={`${crumb.label}-${i}`}
                        sx={{
                          fontSize: "0.9rem",
                          fontWeight: last ? 600 : 500,
                          color: last ? COLORS.typographyColor : "text.secondary",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {crumb.label}
                      </Typography>
                    );
                  }
                  return (
                    <Link
                      key={`${crumb.label}-${i}`}
                      component="button"
                      underline="hover"
                      onClick={() => navigate(crumb.to!)}
                      sx={{
                        fontSize: "0.9rem",
                        fontWeight: 500,
                        color: "text.secondary",
                        verticalAlign: "baseline",
                        transition: "color 0.2s",
                        "&:hover": { color: COLORS.MainColor },
                      }}
                    >
                      {crumb.label}
                    </Link>
                  );
                })}
              </Breadcrumbs>
              <Typography
                sx={{
                  display: { xs: "block", sm: "none" },
                  fontWeight: 600,
                  fontSize: "1rem",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {current?.label}
              </Typography>
            </>
          )}
        </Box>

        <ButtonBase
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label="Account menu"
          aria-haspopup="true"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexShrink: 0,
            borderRadius: 999,
            pl: 0.5,
            pr: { xs: 0.5, sm: 1 },
            py: 0.5,
            border: "1px solid",
            borderColor: anchorEl ? alpha(COLORS.MainColor, 0.4) : "divider",
            bgcolor: anchorEl ? alpha(COLORS.MainColor, 0.06) : "transparent",
            transition: "background-color 0.2s, border-color 0.2s",
            "&:hover": {
              bgcolor: alpha(COLORS.MainColor, 0.06),
              borderColor: alpha(COLORS.MainColor, 0.3),
            },
          }}
        >
          {avatar(32)}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              alignItems: "flex-start",
              maxWidth: 160,
              lineHeight: 1.2,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.85rem",
                fontWeight: 600,
                maxWidth: "100%",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {name || "Account"}
            </Typography>
            {role && (
              <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
                {role}
              </Typography>
            )}
          </Box>
          <ExpandMoreIcon
            sx={{
              display: { xs: "none", sm: "block" },
              fontSize: "1.1rem",
              color: "text.secondary",
              transition: "transform 0.25s cubic-bezier(0.2, 0, 0, 1)",
              transform: anchorEl ? "rotate(180deg)" : "none",
            }}
          />
        </ButtonBase>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                width: 240,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
                "& .MuiMenuItem-root": {
                  mx: 1,
                  borderRadius: 2,
                  fontSize: "0.9rem",
                  py: 1,
                },
                "& .MuiListItemIcon-root": { minWidth: 32 },
              },
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, pt: 1, pb: 1.5 }}>
            {avatar(40)}
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {name || "Account"}
              </Typography>
              {email && (
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    color: "text.secondary",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {email}
                </Typography>
              )}
            </Box>
          </Box>
          <Divider sx={{ mb: 1 }} />
          <MenuItem onClick={() => go("/profile")}>
            <ListItemIcon>
              <PersonOutlineIcon fontSize="small" />
            </ListItemIcon>
            Profile
          </MenuItem>
          <MenuItem onClick={() => go("/edit-venues")}>
            <ListItemIcon>
              <StorefrontOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Edit Venues
          </MenuItem>
          <MenuItem onClick={() => go("/all-data")}>
            <ListItemIcon>
              <DatasetOutlinedIcon fontSize="small" />
            </ListItemIcon>
            All Data
          </MenuItem>
          <Divider sx={{ my: 1 }} />
          <MenuItem
            onClick={handleLogout}
            sx={{
              color: "error.main",
              "&:hover": { bgcolor: (t) => alpha(t.palette.error.main, 0.08) },
            }}
          >
            <ListItemIcon sx={{ color: "inherit" }}>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
